<template>
  <v-container class="edit-profile pa-0">
    <div class="list-header">
      <h3>Your account</h3>
      <div class="actions">
        <span>
          <router-link to="/deals">
            <v-icon medium color="#18305A">mdi-filter-variant</v-icon>
            <span>All deals</span>
          </router-link>
        </span>
        <span>
          <router-link to="/my-deals">
            <v-icon medium color="#18305A">mdi-briefcase-check-outline</v-icon>
            <span>Selected Deals</span>
          </router-link>
          <span class="badge" v-if="registered">{{ registered }}</span>
        </span>
      </div>
    </div>
    <v-container class="form-content">
      <v-row v-if="isInterestIncomplete">
        <v-col cols="12" md="6">
          <v-alert
            outlined
            type="error"
            border="left"
          >
            Please provide missing data to complete your profile
          </v-alert>
        </v-col>
      </v-row>
      <a id="start" style="visibility: none;" />
      <v-form  ref="form" v-model="valid" lazy-validation>
        <v-row>
          <v-col>
            <h5>Contact person</h5>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <p>Register as an investor, buyer, or interested counter-party to review curated opportunities in Africa</p>
            <v-text-field
              v-model="contactPerson.givenName"
              label="Given Name"
              filled
              :rules="rules.required"
            ></v-text-field>
            <v-text-field
              v-model="contactPerson.familyName"
              label="Family Name"
              filled
              :rules="rules.required"
            ></v-text-field>
            <v-text-field
              disabled
              v-model="contactPerson.email"
              label="Email address"
              filled
              :rules="rules.required.concat(rules.email)"
            ></v-text-field>
            <v-text-field
              v-model="contactPerson.telephone"
              label="Phone number"
              filled
              :rules="rules.required.concat(rules.phone)"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h5>Company</h5>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="company.name"
              label="Company Name"
              filled
              :rules="rules.required"
            ></v-text-field>
            <v-text-field
              v-model="company.website"
              label="Website"
              filled
              :rules="rules.required"
            ></v-text-field>
            <v-select
              :items="countryOptions"
              v-model="company.baseCountry"
              filled
              label="Home country"
              item-text="label"
              item-value="label"
              :rules="rules.required"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <p class="group-title">How investor or counterparty meets U.S. Nexus requirements</p>
            <p class="caption">Check all that apply</p>
            <div class="nexus">
              <v-checkbox
                v-for="item in nexusOptions"
                :key="item.value"
                v-model="submission.other.nexus"
                :label="item.name"
                :value="item.value"
                hide-details="auto"
              ></v-checkbox>
              <v-textarea
                v-if="(submission.other.nexus || []).includes('other')"
                filled
                rows="3"
                auto-grow
                hide-details="auto"
                v-model="submission.other.nexusReason"
                label="Describe how meet U.S. nexus requirement"
              ></v-textarea>
            </div>
          </v-col>
        </v-row>
        <v-row class="submit-block">
          <v-col cols="12" md="8">
            <div class="submit-button-wrap">
              <v-btn
                @click="submit"
                :disabled="isLoading"
                x-large
                color="#f0b323">
                Save changes
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-container>

    <v-snackbar timeout="3500" v-model="isMessage">
      {{ messageText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import options from '@/data/options'

const emailRegexp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
const phoneRegexp = /(?:\(?\+\d{2}\)?\s*)?\d+(?:[ -]*\d+)*$/

export default {
  name: 'edit-profile',
  data () {
    return {
      valid: true,
      rules: {
        required: [
          value => !!value || 'Required'
        ],
        email: [
          value => (value && !!value.match(emailRegexp)) || 'Please enter email, e.g john.doe@example.com'
        ],
        phone: [
          value => (value && !!value.match(phoneRegexp)) || 'Please enter valid phone number'
        ]
      },
      isLoading: false,
      errorMessage: '',
      company: {
        name: '',
        website: '',
        baseCountry: ''
      },
      contactPerson: {
        givenName: '',
        familyName: '',
        email: '',
        telephone: ''
      },
      submission: {
        other: {
          nexus: [],
          nexusReason: ''
        }
      },
      isMessage: false,
      messageText: ''
    }
  },
  computed: {
    countryOptions () {
      return options.countries
    },
    nexusOptions () {
      return options.nexus
    },
    interest () {
      return this.$store.getters['deals/interest'] || {}
    },
    isInterestIncomplete () {
      return this.$store.getters['deals/isInterestIncomplete']
    },
    registered () {
      return ((this.interest && this.interest.deals) || []).length
    }
  },
  mounted () {
    if (this.interest) {
      this.fillForm()
    }
  },
  watch: {
    interest (value) {
      if (value) {
        this.fillForm()
      }
    }
  },
  methods: {
    filterSpecified (object) {
      return Object.keys(object).reduce((result, key) => {
        if (object[key] !== '<not specified>') {
          result[key] = object[key]
        }
        return result
      }, {})
    },
    fillForm () {
      if (this.interest.company) {
        this.company = this.filterSpecified(this.interest.company)
      }
      if (this.interest.contactPerson) {
        this.contactPerson = this.filterSpecified(this.interest.contactPerson)
      }
      this.submission.other = { ...this.interest.other }
      this.submission.other.nexus = [].concat(this.submission.other.nexus)
    },
    createInterestPayload () {
      const payload = Object.assign(this.interest, {
        other: this.submission.other,
        company: this.company,
        contactPerson: this.contactPerson
      })

      return payload
    },
    submit () {
      const valid = this.$refs.form.validate()
      if (!valid) {
        location.hash = ''
        this.messageText = 'Please correct validation errors above'
        this.isMessage = true
        location.hash = 'start'
        return
      }

      this.$ga.event({
        eventCategory: 'Click',
        eventAction: 'update-profile',
        eventLabel: 'Update profile'
      })

      this.isLoading = true
      this.errorMessage = ''

      const payload = this.createInterestPayload()

      this.$store.dispatch('deals/update', { interest: payload }).then(() => {
        this.$emit('submitted')
        this.isLoading = false
        setTimeout(() => {
          this.$router.push('/my-deals')
        }, 3500)
      }).catch(err => {
        this.messageText = (err && err.data && err.data.message) || 'Failed to submit the data. Please correct validation errors above'
        this.isMessage = true
        this.isLoading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
.edit-profile {
  position: relative;
  background: #fff;
  box-shadow: 0px 0px 14px rgba(59, 73, 86, 0.15);
  border-radius: 6px;
  margin-bottom: 50px;
  .form-content {
    padding-left: 50px;
  }
}
.list-header {
  width: 100%;
  padding: 30px;
  font-size: 20px;
  font-family: $header-font;
  color: $text-primary;
  background: #EBF5FF;
  @media (min-width: 800px) {
    font-size: 36px;
  }
  .actions {
    span {
      margin-right: 20px;
      display: inline-flex;
      align-items: center;
    }
    a {
      display: inline-flex;
      align-items: center;
      font-style: normal;
      font-weight: bold;
      font-size: 14px;
      line-height: 130%;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #18305A;
      text-decoration: none;
      i {
        margin-right: 5px;
      }
      span {
        text-decoration-line: underline;
        margin: 0px;
      }
    }
    .badge {
      display: inline-block;
      width: 28px;
      height: 28px;
      background: #18305A;
      border-radius: 30px;
      margin-left: 5px;

      font-style: normal;
      font-size: 14px;
      font-weight: bold;
      line-height: 26px;
      text-align: center;
      letter-spacing: 0.06em;
      color: #FFFFFF;
    }
  }
}
h5 {
  color: $text-primary;
  font-family: $header-font;
  font-size: 20px;
  line-height: 140%;
  font-weight: 500;
}
.v-input--checkbox {
  margin-top: 0px;
}
.v-text-field {
  & ::v-deep .v-text-field__details {
    display: block;
  }
  font-weight: 400;
  font-size: 18px;
  line-height: 27px;
  margin-bottom: 5px!important;
}
.v-input {
  .v-label {
    line-height: 27px!important;
    font-size: 18px;
    color: #93979D;
  }
}
.main-checkbox {
  & ::v-deep label {
    font-weight: bold;
  }
}
.group-title {
  color: $gray-primary;
  font-size: 18px;
  line-height: 150%;
  margin-bottom: 0px;
}
.caption {
  color: #93979D;
}
.submit-block {
  background: #fff9e6;
  padding-top: 15px;
  padding-bottom: 15px;
  padding-left: 50px;
  margin-left: -50px;
  margin-bottom: 50px;
  .caption {
    margin-bottom: 0px;
  }
}
.v-text-field__slot {
  &::before {
    bottom: 0;
  }
}
.nexus {
  .v-input {
    margin: 10px 0;
  }
}
.submit-button-wrap {
  display: inline-block;
  position: relative;
  .login {
    position: absolute;
    bottom: -45px;
    left: 15px;
    color: #18305A;
    font-size: 14px;
    a {
      font-weight: bold;
    }
  }
}
</style>
